import AnalyticsCover from "./AnalyticsCover";
import { loadAnalyticsCovers } from "../api/loadAnalyticsCovers";
import useAnalyticsData from "../hooks/useAnalyticsData";
import { useCallback, useEffect, useMemo, useState } from "react";
import { analyticsApi, analyticsPaths } from "../api/analyticsApi";
import { instagramApi } from "../api/instagramApi";
import { useToast } from "../context/ToastContext";
import { useModal } from "../context/ModalContext";
import { LoadingState, ErrorState } from "./ApiState";
import EmptyState from "./EmptyState";
import { fmtDay, fmtDateTime } from "../utils/periodShared";
import { sortNumericRows } from "../utils/numericSort";

const TYPE_OPTIONS = [["", "All types"], ["REEL", "Reel"], ["POST", "Post"], ["CAROUSEL", "Carousel"]];
const PAGE = 20;
// Set to true to restore the comment-access notice and reconnect button.
const SHOW_COMMENT_ACCESS_NOTICE = false;
// Reply-performance colours: on time = green, delayed reply = orange, delayed and still not replied = red.
const GREEN = "#1fac71", RED = "#e5383b", ORANGE = "#f97316", MUTED = "#8f8f8a";

const STATUS = {
  on_time: { label: "Replied on Time", color: GREEN },
  delayed_replied: { label: "Replied late", color: ORANGE },
  delayed_not_replied: { label: "Not Replied Yet", color: RED },
  awaiting: { label: "Awaiting Reply", color: MUTED },
  unverified: { label: "Not verified", color: MUTED },
  no_reply_needed: { label: "No Reply Needed", color: MUTED },
};
const DETAIL_PAGE = 15;
// Accept emoji sequences (including flags, skin tones, keycaps and joined emoji), separated by whitespace.
const EMOJI_ONLY = /^(?:\s|\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3|\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\p{Emoji_Modifier})?)*)+$/u;
export function commentMatchesFilter(row, filter) {
  if (filter === "all") return true;
  if (Object.prototype.hasOwnProperty.call(row, "category")) return row.category === filter;
  const text = String(row.comment_full || row.comment || "").trim();
  const emojiOnly = text !== "" && EMOJI_ONLY.test(text);
  if (filter === "emoji") return emojiOnly;
  return filter === "word" && !emojiOnly && /[\p{L}\p{N}]/u.test(text);
}

/** The comments of ONE post / Reel / Carousel: the exact first line of each comment, when it arrived, when we first replied, and its 24-hour status. Read only. */
export function CommentsDetail({ accountId, item, cover, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const load = useCallback(async (quiet) => {
    try { setData(await analyticsApi.commentsContent(accountId, item.instagram_media_id)); setError(null); }
    catch (e) { if (!quiet) setError(e.message || "Could not load the comments."); }
  }, [accountId, item.instagram_media_id]);
  useEffect(() => { load(false); }, [load]);
  // The server re-reads this post and re-classifies at the 24-hour deadline; the view just re-asks every minute while it is open.
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 60000);
    return () => clearInterval(t);
  }, [load]);
  const tz = data?.timezone;
  const rows = data?.rows || [];
  const filteredRows = rows.filter((row) => commentMatchesFilter(row, filter));
  const pages = Math.max(1, Math.ceil(filteredRows.length / DETAIL_PAGE));
  const currentPage = Math.min(page, pages);
  const view = filteredRows.slice((currentPage - 1) * DETAIL_PAGE, currentPage * DETAIL_PAGE);
  return (
    <div className="an-detail cm-detail">
      <button className="modal-close" onClick={onClose} aria-label="Close"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
      <div className="an-detail-head">
        {cover ? <img className="an-detail-cover" src={cover} alt="" /> : <span className="an-detail-cover an-detail-cover-empty" aria-hidden="true" />}
        <div className="an-detail-title">
          <div className="tag tag-post">{item.kind_label}</div>
          <h3>{item.description || "Comments"}</h3>
          <div className="an-detail-date">{fmtDay(item.published_at, tz || item.tz)}</div>
        </div>
      </div>
      {!data && !error && <LoadingState label="Loading comments..." />}
      {error && <ErrorState title="Unable to load comments" message={error} onRetry={() => load(false)} />}
      {data && (rows.length === 0 ? (
        <div className="metrics-note">No comments have been read for this content yet.</div>
      ) : (
        <>
          <div className="cm-detail-filters">
            <label htmlFor="comment-text-filter">Filter comments</label>
            <select id="comment-text-filter" className="select-filter" value={filter} onChange={(e) => { setFilter(e.target.value); setPage(1); }}>
              <option value="all">All</option><option value="word">Word</option><option value="emoji">Emoji</option>
            </select>
          </div>
          {filteredRows.length === 0 ? <div className="metrics-note">No comments match this filter.</div> : <>
          <div className="metrics-table-wrap">
            <table className="metrics-table an-center cm-detail-table">
              <thead><tr><th className="cm-sno">S.No</th><th className="cm-left">Comments</th><th>Comments Received</th><th>Comment Replied</th><th>Status</th></tr></thead>
              <tbody>{view.map((r, i) => {
                const st = commentMatchesFilter(r, "emoji") ? STATUS.no_reply_needed : STATUS[r.status] || STATUS.awaiting;
                return (
                  <tr key={r.key}>
                    <td className="cm-sno">{(currentPage - 1) * DETAIL_PAGE + i + 1}</td>
                    <td className="cm-left cm-text" title={r.comment_full || r.comment}>{r.comment || "—"}</td>
                    <td>{fmtDateTime(r.received_at, tz)}</td>
                    <td>{r.reply_at ? fmtDateTime(r.reply_at, tz) : "Not Replied"}</td>
                    <td><span className="ou-pill" style={{ borderColor: st.color, color: st.color }}>{st.label}</span></td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
          {filteredRows.length > DETAIL_PAGE && (
            <div className="metrics-pager">
              <button className="btn btn-ghost btn-sm" disabled={currentPage <= 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
              <span>Page {currentPage} of {pages}</span>
              <button className="btn btn-ghost btn-sm" disabled={currentPage >= pages} onClick={() => setPage(currentPage + 1)}>Next</button>
            </div>
          )}
          </>}
        </>
      ))}
      <div className="modal-actions"><button className="btn btn-primary btn-block" onClick={onClose}>Close</button></div>
    </div>
  );
}

function Count({ value, color }) {
  return <strong style={{ color: value > 0 ? color : MUTED, fontWeight: value > 0 ? 800 : 600 }}>{value}</strong>;
}

/**
 * Analytics > Comments. READ ONLY (never replies to, posts, edits or deletes a comment).
 * One row per published Post / Reel / Carousel; the first five columns reuse Content Performance's table, cover loader and
 * formatting. Reply counts cover Word comments: on-time replies within 24 h, later replies, and known unanswered comments.
 */
export default function CommentsSection({ accountId }) { // the account chosen at the top of Analytics
  const { showToast } = useToast();
  const { openModal, closeModal } = useModal();
  const [month, setMonth] = useState("current");
  const [type, setType] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [connectingComments, setConnectingComments] = useState(false);
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const [page, setPage] = useState(1);
  const [covers, setCovers] = useState({});
  const [coverErrors, setCoverErrors] = useState({});

  const report = useAnalyticsData(accountId ? analyticsPaths.comments(accountId,month,type) : null, 30000);
  const { data, error, loading } = report;
  const load = report.refresh;
  useEffect(() => { setPage(1); setCovers({}); setCoverErrors({}); }, [accountId,month,type]);

  const refresh = async () => {
    if (refreshing || report.refreshing || data?.refreshing) return;
    setRefreshing(true);
    try { await analyticsApi.commentsRefresh(accountId, month, type); showToast("Comment refresh started. Stored data stays visible while Instagram is checked."); }
    catch (e) { showToast(e.message || "Could not refresh."); }
    finally { setRefreshing(false); }
  };
  const reconnectComments = async () => {
    if (connectingComments) return;
    setConnectingComments(true);
    try { await instagramApi.startConnect({ requestComments: true, accountId }); }
    catch (e) { setConnectingComments(false); showToast(e.message || 'Could not start the Instagram connection.'); }
  };

  // The server re-reads Instagram in the background and re-classifies at every 24-hour deadline; the page just re-asks every minute.
  useEffect(() => {
    const poll = setInterval(() => { if (document.visibilityState === "visible") load(true); }, 60000);
    return () => clearInterval(poll);
  }, [load]);

  const openDetail = (r) => openModal(<CommentsDetail accountId={accountId} item={{ ...r, tz: data?.timezone }} cover={covers[r.instagram_media_id] || analyticsApi.cachedCover(accountId, r.instagram_media_id)} onClose={closeModal} />);
  const rows = useMemo(() => data?.rows || [], [data]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const sortedRows = useMemo(() => sort.key ? sortNumericRows(rows, r => r[sort.key], sort.dir) : rows, [rows, sort]);
  const view = useMemo(() => sortedRows.slice((page - 1) * PAGE, page * PAGE), [sortedRows, page]);
  const sortComments = (key) => { setSort(s => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" })); setPage(1); };
  const tz = data?.timezone;
  const sync = data?.sync;
  const coverIds = view.map(r => String(r.instagram_media_id)).join(',');

  // Covers for the visible rows: same live loader Content Performance uses (kept in memory only).
  useEffect(() => {
    const ids = coverIds.split(',').filter(id => id && !covers[id] && !analyticsApi.cachedCover(accountId, id));
    if (!ids.length || !accountId) return undefined;
    let cancelled = false;
    loadAnalyticsCovers(ids, batch => analyticsApi.covers(accountId, batch), r => {
      setCovers(c => ({ ...c, ...r.covers }));
      setCoverErrors(c => ({ ...c, ...(r.errors || {}) }));
    }, () => cancelled);
    return () => { cancelled = true; };
  }, [coverIds, accountId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className="metrics-section ou-section" id="comments">
      <div className="metrics-table-head">
        <h3 className="metrics-h3" style={{ margin: 0 }}>Comments</h3>
        <div className="metrics-table-filters">
          <select className="select-filter" aria-label="Month" title="Filter by the month the content was published" value={data ? data.month : "current"} onChange={(e) => setMonth(e.target.value)}>
            {!data && <option value="current">This Month</option>}
            {(data?.months || []).map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            <option value="all">All Months</option>
          </select>
          <select className="select-filter" aria-label="Content type" title="Filter by content type" value={type} onChange={(e) => setType(e.target.value)}>
            {TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {sync?.last_success_at && <span className="metrics-sync-text" aria-live="polite">Last refreshed {fmtDateTime(sync.last_success_at, tz)}</span>}
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={refreshing || report.refreshing || data?.refreshing} title="Read the latest comments from Instagram (read only)">{refreshing ? "Refreshing…" : "Refresh"}</button>
        </div>
      </div>

      {SHOW_COMMENT_ACCESS_NOTICE && sync?.status === "NO_PERMISSION" && <div className="metrics-note">
        <p>{/comment access was granted|DOES include instagram_manage_comments/i.test(sync.error || '')
          ? `Meta could not read comments for @${data.account.username}. Check the linked Facebook Page and Meta app access; you can also reconnect the account.`
          : `Instagram comment access is unavailable for @${data.account.username}. Reconnect the account and grant comment access. Meta may ask for permission to manage comments; this CRM uses it to read comments and replies.`}
          {sync.last_success_at && ' Previously synced comments may be out of date.'}</p>
        <button type="button" className="btn btn-secondary btn-sm" onClick={reconnectComments} disabled={connectingComments}>{connectingComments ? 'Connecting...' : 'Reconnect for comments'}</button>
      </div>}
      {sync && ["AUTH", "ERROR"].includes(sync.status) && <div className="metrics-note">@{data.account.username}: {sync.error || "Comments could not be read."}</div>}
      {sync?.status === "PENDING" && <div className="metrics-caption" role="status">Checking Instagram comment access...</div>}
      {sync && sync.status === "NEVER" && <div className="metrics-note">Comments have not been read yet for @{data.account.username}. Press Refresh.</div>}

      {(report.refreshing || data?.refreshing) && data && <div className="metrics-caption" role="status">Refreshing...</div>}
      {report.notice && <div className="metrics-caption" role="status">{report.notice}</div>}
      {error && data && <div className="metrics-note">Showing last available comments. {error}</div>}
      {loading && !data ? <LoadingState label="Loading comments..." /> : error && !data ? <ErrorState title="Unable to load comments" message={error} onRetry={() => load(false)} /> : data && (
        rows.length === 0 ? (
          <EmptyState title="No published content found for these filters." desc="Posts, Reels and Carousels appear here once Analytics has read them from Instagram." />
        ) : (
          <>
            <div className="metrics-table-wrap">
              <table className="metrics-table metrics-content-table an-center cm-table">
                <colgroup>
                  <col className="col-date" /><col className="col-cover" /><col className="col-desc" /><col className="col-type" />
                  <col className="col-num" /><col className="col-num" /><col className="col-num" /><col className="col-num" /><col className="col-num" /><col className="col-num" />
                </colgroup>
                <thead><tr>
                  <th>Published Date</th><th>Cover</th><th>Content Description</th><th>Content Type</th>
                  {[["total", "Total Comments"], ["emoji", "Emoji"], ["word", "Word"], ["on_time", "Replied on Time"], ["delayed_replied", "Replied late"], ["delayed_not_replied", "Not Replied Yet"]].map(([key, label]) => <th key={key} className="is-num" aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}><button type="button" className="metrics-sort-btn" onClick={() => sortComments(key)}>{label}{sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}</button></th>)}
                </tr></thead>
                <tbody>{view.map((r) => (
                  <tr key={r.instagram_media_id} className="metrics-row" tabIndex={0} title="Click to see this content's comments" onClick={() => openDetail(r)} onKeyDown={(e) => { if (e.key === "Enter") openDetail(r); }}>
                    <td>{fmtDay(r.published_at, tz)}</td>
                    <td className="metrics-cover-cell"><AnalyticsCover accountId={accountId} mediaId={r.instagram_media_id} src={covers[r.instagram_media_id] || analyticsApi.cachedCover(accountId, r.instagram_media_id)} title={coverErrors[r.instagram_media_id]} /></td>
                    <td title={r.description}>{r.description}</td>
                    <td>{r.kind_label}</td>
                    <td className="is-num"><strong>{r.total}</strong></td>
                    <td className="is-num"><strong>{r.emoji ?? "—"}</strong></td>
                    <td className="is-num"><strong>{r.word ?? "—"}</strong></td>
                    <td className="is-num"><Count value={r.on_time} color={GREEN} /></td>
                    <td className="is-num"><Count value={r.delayed_replied} color={ORANGE} /></td>
                    <td className="is-num"><Count value={r.delayed_not_replied} color={RED} /></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {rows.length > PAGE && (
              <div className="metrics-pager">
                <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                <span>Page {page} of {pages}</span>
                <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
              </div>
            )}
          </>
        )
      )}
    </section>
  );
}
